import React, { useState } from 'react';
import { fruits } from '../data/gameData';

const FruitPlanner = ({ darkMode }) => {
  const [inventory, setInventory] = useState([]);
  const [playstyle, setPlaystyle] = useState('pve');
  const [selectedFruitId, setSelectedFruitId] = useState('');
  
  const addFruitToInventory = () => {
    if (!selectedFruitId) return;
    
    const fruit = fruits.find(f => f.id === selectedFruitId);
    if (fruit && !inventory.some(f => f.id === fruit.id)) {
      setInventory([...inventory, fruit]);
      setSelectedFruitId('');
    }
  };
  
  const removeFruitFromInventory = (fruitId) => {
    setInventory(inventory.filter(fruit => fruit.id !== fruitId));
  };
  
  const getRecommendedFruits = () => {
    // This would normally use more complex logic based on playstyle
    switch (playstyle) {
      case 'pvp':
        return fruits.filter(fruit => fruit.pvpRating >= 8);
      case 'pve':
        return fruits.filter(fruit => fruit.pveRating >= 8);
      case 'farming':
        return fruits.filter(fruit => fruit.farmingRating >= 8);
      case 'raiding':
        return fruits.filter(fruit => fruit.raidingRating >= 8);
      default:
        return [];
    }
  };
  
  const recommendedFruits = getRecommendedFruits();
  
  const cardClass = darkMode 
    ? 'bg-card-dark shadow-card-dark border border-gray-700' 
    : 'bg-white shadow-card-light';
    
  const headingClass = darkMode 
    ? 'text-gray-100' 
    : 'text-gray-900';
    
  const textClass = darkMode 
    ? 'text-gray-300' 
    : 'text-gray-600';
    
  const inputClass = darkMode 
    ? 'bg-gray-800 border-gray-700 text-gray-200' 
    : 'border-gray-300 text-gray-900';
    
  const dividerClass = darkMode 
    ? 'border-gray-700' 
    : 'border-gray-200';
  
  return (
    <div>
      <h1 className={`text-3xl font-bold mb-6 ${headingClass}`}>Fruit Acquisition Planner</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`p-6 rounded-lg transition-theme duration-300 ease-in-out ${cardClass}`}>
          <h2 className={`text-2xl font-bold mb-4 ${headingClass}`}>Your Inventory</h2>
          
          <div className="mb-6">
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Add Fruit to Inventory:
            </label>
            <div className="flex">
              <select 
                className={`flex-grow p-2 border rounded-l transition-colors ${inputClass}`}
                value={selectedFruitId}
                onChange={(e) => setSelectedFruitId(e.target.value)}
              >
                <option value="" disabled>Select a fruit</option>
                {fruits.map(fruit => (
                  <option 
                    key={fruit.id} 
                    value={fruit.id}
                    disabled={inventory.some(f => f.id === fruit.id)}
                  >
                    {fruit.name}
                  </option>
                ))}
              </select>
              <button 
                onClick={addFruitToInventory}
                disabled={!selectedFruitId}
                className={`px-4 py-2 rounded-r font-medium transition-colors ${
                  darkMode 
                    ? 'bg-primary-dark text-white hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400' 
                    : 'bg-primary-light text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-500'
                }`}
              >
                Add
              </button>
            </div>
          </div>
          
          {inventory.length === 0 ? (
            <div className={`text-center py-12 border-2 border-dashed rounded-lg ${darkMode ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="mt-2">No fruits in inventory. Add fruits to track them.</p>
            </div>
          ) : (
            <ul className={`divide-y ${dividerClass}`}>
              {inventory.map(fruit => (
                <li key={fruit.id} className="py-4 flex justify-between items-center">
                  <div>
                    <span className={`font-medium ${headingClass}`}>{fruit.name}</span>
                    <p className={`text-sm mt-1 ${textClass}`}>Value: {fruit.value.toLocaleString()} Beli</p>
                  </div>
                  <button 
                    className={`p-2 rounded-full hover:bg-opacity-10 ${
                      darkMode ? 'text-red-400 hover:bg-red-800' : 'text-red-600 hover:bg-red-100'
                    }`}
                    onClick={() => removeFruitFromInventory(fruit.id)}
                    aria-label={`Remove ${fruit.name}`}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        
        <div className={`p-6 rounded-lg transition-theme duration-300 ease-in-out ${cardClass}`}>
          <h2 className={`text-2xl font-bold mb-4 ${headingClass}`}>Fruit Recommendations</h2>
          
          <div className="mb-6">
            <label className={`block text-sm font-medium ${textClass} mb-1`}>
              Select Your Playstyle:
            </label>
            <select
              className={`w-full p-2 border rounded transition-colors ${inputClass}`}
              value={playstyle}
              onChange={(e) => setPlaystyle(e.target.value)}
            >
              <option value="pve">PvE (Farming/Grinding)</option>
              <option value="pvp">PvP (Player vs Player)</option>
              <option value="farming">Farming (Resources/Money)</option>
              <option value="raiding">Raiding (Bosses/Dungeons)</option>
            </select>
          </div>
          
          <h3 className={`font-semibold text-lg mb-2 ${headingClass}`}>Recommended Fruits:</h3>
          
          {recommendedFruits.length > 0 ? (
            <ul className={`divide-y ${dividerClass} rounded-lg overflow-hidden border ${dividerClass}`}>
              {recommendedFruits.map(fruit => (
                <li key={fruit.id} className={`py-3 px-4 ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                  <div className="flex justify-between">
                    <span className={`font-medium ${headingClass}`}>{fruit.name}</span>
                    <span className={`font-semibold ${
                      darkMode 
                        ? getRatingColorDark(playstyle === 'pvp' ? fruit.pvpRating : playstyle === 'pve' ? fruit.pveRating : playstyle === 'farming' ? fruit.farmingRating : fruit.raidingRating) 
                        : getRatingColorLight(playstyle === 'pvp' ? fruit.pvpRating : playstyle === 'pve' ? fruit.pveRating : playstyle === 'farming' ? fruit.farmingRating : fruit.raidingRating)
                    }`}>
                      Rating: {
                        playstyle === 'pvp' ? fruit.pvpRating :
                        playstyle === 'pve' ? fruit.pveRating :
                        playstyle === 'farming' ? fruit.farmingRating :
                        fruit.raidingRating
                      }/10
                    </span>
                  </div>
                  <p className={`text-sm mt-1 ${textClass}`}>{fruit.description}</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className={`text-center py-8 ${textClass}`}>
              No fruits match your playstyle requirements.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions for color-coding ratings
function getRatingColorLight(rating) {
  if (rating >= 9) return 'text-green-600';
  if (rating >= 7) return 'text-blue-600';
  if (rating >= 5) return 'text-yellow-600';
  return 'text-red-600';
}

function getRatingColorDark(rating) {
  if (rating >= 9) return 'text-green-400';
  if (rating >= 7) return 'text-blue-400';
  if (rating >= 5) return 'text-yellow-400';
  return 'text-red-400';
}

export default FruitPlanner;
